<template>
    <div class="invite-page">
        <div class="invite-container">
            <div class="page-header">
                <Button icon="ios-arrow-back" @click="$router.back()" type="text">返回</Button>
                <h2>邀请好友</h2>
            </div>

            <div class="invite-hero pink-gradient">
                <div class="hero-content">
                    <h1>分享获取积分奖励</h1>
                    <p>邀请好友加入，开启智慧学习之旅</p>

                    <div class="invite-code-box">
                        <div class="label">我的邀请码</div>
                        <div class="code">{{ inviteInfo.invite_code }}</div>
                        <Button type="primary" shape="circle" icon="md-copy"
                            @click="copyText(inviteInfo.invite_code)">复制邀请码</Button>
                    </div>

                    <div class="invite-link-box">
                        <Input v-model="inviteLink" readonly size="large" class="link-input">
                        <template #append>
                            <Button type="primary" @click="copyText(inviteLink)">复制链接</Button>
                        </template>
                        </Input>
                    </div>
                </div>
            </div>

            <div class="stats-row">
                <div class="stat-card">
                    <div class="val">{{ inviteInfo.total_invited }}</div>
                    <div class="label">累计邀请人数</div>
                </div>
                <div class="stat-card">
                    <div class="val">{{ inviteInfo.rewarded_pay }}</div>
                    <div class="label">首充奖励次数</div>
                </div>
                <div class="stat-card">
                    <div class="val">{{ inviteInfo.total_rewarded_points || 0 }}</div>
                    <div class="label">累计获得积分</div>
                </div>
            </div>

            <div class="rules-section glass-effect">
                <h3>奖励规则</h3>
                <ul class="rule-list">
                    <li>
                        <Icon type="md-checkmark-circle" color="#19be6b" />
                        好友通过您的链接或邀请码注册，您可获得 <strong>50 积分</strong>。
                    </li>
                    <li>
                        <Icon type="md-checkmark-circle" color="#19be6b" />
                        受邀好友首次在平台充值积分，您可额外获得 <strong>100 积分</strong>。
                    </li>
                    <li>
                        <Icon type="md-checkmark-circle" color="#19be6b" />
                        奖励实时到账，可在“积分流水”中查看。
                    </li>
                </ul>
            </div>

            <div class="records-section glass-effect">
                <h3>邀请记录</h3>
                <Table :columns="columns" :data="records" no-data-text="暂无邀请记录" stripe size="small"></Table>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import api from '@/utils/api'
import { Message } from 'view-ui-plus'

const inviteInfo = ref({
    invite_code: '',
    total_invited: 0,
    rewarded_pay: 0,
    total_rewarded_points: 0
})
const records = ref([])

const inviteLink = computed(() => {
    const base = window.location.origin + window.location.pathname.replace(/\/invite-friend$/, '')
    return base + '/register?invite=' + inviteInfo.value.invite_code
})

const columns = [
    { title: '受邀用户', key: 'invitee_username', minWidth: 120 },
    {
        title: '注册时间',
        key: 'created_at',
        render: (h, { row }) => h('span', formatDate(row.created_at))
    },
    {
        title: '首充奖励',
        render: (h, { row }) => h('Tag', {
            props: { color: row.first_pay_rewarded ? 'success' : 'default' }
        }, row.first_pay_rewarded ? '已发放' : '未达成')
    }
]

const fetchInviteData = async () => {
    try {
        const res = await api.get('/api/invite/code')
        inviteInfo.value = res.data
        const recRes = await api.get('/api/invite/records')
        records.value = recRes.data.records || []
    } catch (e) {
        Message.error('获取邀请数据失败')
    }
}

const copyText = async (text) => {
    if (!navigator.clipboard || !navigator.clipboard.writeText) {
        Message.error('当前浏览器不支持自动复制')
        return
    }

    try {
        await navigator.clipboard.writeText(text)
        Message.success('已复制到剪贴板')
    } catch (e) {
        Message.error('复制失败，请检查浏览器权限')
    }
}

const formatDate = (ts) => {
    if (!ts) return '-'
    const d = new Date(ts * 1000)
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

onMounted(fetchInviteData)
</script>

<style scoped>
.invite-page {
    min-height: calc(100vh - 64px);
    background: #f8f9fa;
    padding: 40px 20px;
}

.invite-container {
    max-width: 900px;
    margin: 0 auto;
}

.page-header {
    display: flex;
    align-items: center;
    gap: 15px;
    margin-bottom: 24px;
}

.invite-hero {
    border-radius: 24px;
    padding: 60px 40px;
    text-align: center;
    color: #fff;
    margin-bottom: 30px;
    box-shadow: 0 10px 30px rgba(245, 87, 108, 0.2);
}

.pink-gradient {
    background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
}

.hero-content h1 {
    font-size: 32px;
    margin-bottom: 12px;
}

.hero-content p {
    font-size: 16px;
    opacity: 0.9;
    margin-bottom: 40px;
}

.invite-code-box {
    background: rgba(255, 255, 255, 0.15);
    backdrop-filter: blur(5px);
    border-radius: 16px;
    padding: 24px;
    display: inline-block;
    min-width: 300px;
    margin-bottom: 30px;
}

.invite-code-box .label {
    font-size: 14px;
    margin-bottom: 8px;
    opacity: 0.8;
}

.invite-code-box .code {
    font-size: 48px;
    font-weight: 800;
    letter-spacing: 6px;
    margin-bottom: 15px;
}

.invite-link-box {
    max-width: 500px;
    margin: 0 auto;
}

.link-input :deep(.ivu-input) {
    background: rgba(255, 255, 255, 0.9);
    border-radius: 20px 0 0 20px;
}

.stats-row {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;
    margin-bottom: 30px;
}

.stat-card {
    background: #fff;
    padding: 24px;
    border-radius: 16px;
    text-align: center;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);
}

.stat-card .val {
    font-size: 28px;
    font-weight: 800;
    color: #1a1a1a;
}

.stat-card .label {
    font-size: 13px;
    color: #808695;
    margin-top: 4px;
}

.rules-section,
.records-section {
    background: #fff;
    padding: 30px;
    border-radius: 16px;
    margin-bottom: 30px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);
}

.glass-effect {
    background: rgba(255, 255, 255, 0.8) !important;
    backdrop-filter: blur(10px);
}

h3 {
    font-size: 18px;
    margin-bottom: 20px;
    color: #1a1a1a;
}

.rule-list {
    list-style: none;
}

.rule-list li {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 12px;
    font-size: 14px;
}
</style>
